import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Clock, RotateCcw, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatearFecha } from '../../i18n/fechas';
import './turnosLista.css';

const MonthlyCalendarView = ({ monthData, onRefresh, onClaseClick, cambiosDisponibles }) => {
  // i18n zona cliente (interior CA · F2): namespace gestor
  const { t } = useTranslation('gestor');
  const [, forceUpdate] = useState(0);
  const timersRef = useRef({});
  const lastBackendSyncRef = useRef({});
  const isVisibleRef = useRef(true);
  
  // Debug: Log classes with grace period
  useEffect(() => {
    const gracePeriodClasses = (monthData.clases_del_mes || []).filter(c => c.is_in_grace_period);
    if (gracePeriodClasses.length > 0) {
      console.log('🔵 Classes in grace period:', gracePeriodClasses.map(c => ({
        fecha: c.fecha,
        hora: c.hora,
        is_in_grace_period: c.is_in_grace_period,
        grace_expires_at: c.grace_expires_at,
        grace_remaining_seconds: c.grace_remaining_seconds
      })));
    }
  }, [monthData]);
  
  // Calculate remaining seconds based on grace_expires_at timestamp
  const calculateRemainingSeconds = (graceExpiresAt) => {
    if (!graceExpiresAt) return 0;
    
    // Parse backend UTC timestamp correctly
    // Backend sends ISO string in UTC, we need to parse it as UTC
    const expiresAt = new Date(graceExpiresAt);
    const now = new Date();
    
    // Both are now in local time, calculate difference
    const remaining = Math.floor((expiresAt - now) / 1000);
    
    // Debug logging (can be removed later)
    if (remaining > 0 && remaining <= 60) {
      console.log('Grace period remaining:', remaining, 'seconds for', graceExpiresAt);
    }
    
    return Math.max(0, remaining);
  };
  
  // Format time as mm:ss
  const formatGraceTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle visibility change (tab focus/blur)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isVisibleRef.current = isVisible;
      
      if (isVisible) {
        // Recalibrate when tab becomes visible
        forceUpdate(n => n + 1);
        
        // Check if we need to refresh from backend (if any timer has drifted >2s)
        const needsSync = Object.entries(lastBackendSyncRef.current).some(([cambioId, lastSync]) => {
          const timeSinceSync = Date.now() - lastSync;
          return timeSinceSync > 5000; // Refresh if >5s since last backend sync
        });
        
        if (needsSync && onRefresh) {
          onRefresh();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [onRefresh]);
  
  // Setup timers for all grace period classes
  useEffect(() => {
    // Clear all existing timers
    Object.values(timersRef.current).forEach(timer => clearInterval(timer));
    timersRef.current = {};
    
    // Track which classes need timers
    const activeGraceClasses = new Set();
    
    (monthData.clases_del_mes || []).forEach(clase => {
      if (clase.is_in_grace_period && clase.grace_expires_at) {
        const remaining = calculateRemainingSeconds(clase.grace_expires_at);
        
        if (remaining > 0) {
          activeGraceClasses.add(clase.cambio_id);
          lastBackendSyncRef.current[clase.cambio_id] = Date.now();
          
          // Create interval for this specific class
          if (!timersRef.current[clase.cambio_id]) {
            timersRef.current[clase.cambio_id] = setInterval(() => {
              // Only update if tab is visible
              if (isVisibleRef.current) {
                const newRemaining = calculateRemainingSeconds(clase.grace_expires_at);
                
                if (newRemaining <= 0) {
                  // Timer expired - clear it and refresh data
                  clearInterval(timersRef.current[clase.cambio_id]);
                  delete timersRef.current[clase.cambio_id];
                  delete lastBackendSyncRef.current[clase.cambio_id];
                  
                  // Refresh from backend to get updated state
                  if (onRefresh) {
                    setTimeout(() => onRefresh(), 500);
                  }
                }
                
                // Force re-render to update displayed time
                forceUpdate(n => n + 1);
              }
            }, 1000);
          }
        }
      }
    });
    
    // Clean up timers for classes that no longer need them
    Object.keys(timersRef.current).forEach(cambioId => {
      if (!activeGraceClasses.has(cambioId)) {
        clearInterval(timersRef.current[cambioId]);
        delete timersRef.current[cambioId];
        delete lastBackendSyncRef.current[cambioId];
      }
    });
    
    // Cleanup on unmount
    return () => {
      Object.values(timersRef.current).forEach(timer => clearInterval(timer));
      timersRef.current = {};
    };
  }, [monthData, onRefresh]);
  
  // Get current remaining time for a specific class (recalculated every render)
  const getGraceRemaining = (clase) => {
    if (!clase.is_in_grace_period || !clase.grace_expires_at) {
      return 0;
    }
    const remaining = calculateRemainingSeconds(clase.grace_expires_at);
    
    // Debug: log when grace period is active
    if (clase.is_in_grace_period && remaining === 0) {
      console.log('Grace period expired for class:', clase.fecha, clase.hora, 'expires_at:', clase.grace_expires_at);
    }
    
    return remaining;
  };
  // i18n F2: fechas por el helper (locale = idioma activo es/ca).
  const formatDate = (dateStr) => formatearFecha(dateStr + 'T00:00:00', {
    weekday: 'short',
    day: '2-digit',
    month: 'short'
  });

  const formatFullDate = (dateStr) => formatearFecha(dateStr + 'T00:00:00', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  // Group classes by date (SHOW ALL classes including past ones)
  const clasesPorFecha = {};
  (monthData.clases_del_mes || [])
    .forEach(clase => {  // Remove filter - show all classes including caducadas
      if (!clasesPorFecha[clase.fecha]) {
        clasesPorFecha[clase.fecha] = [];
      }
      clasesPorFecha[clase.fecha].push(clase);
    });

  // Sort dates
  const fechasOrdenadas = Object.keys(clasesPorFecha).sort();

  if (fechasOrdenadas.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 mb-4">{t('cal.sinClases')}</p>
          <p className="text-sm text-gray-400">
            {t('cal.sinClasesSub')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white shadow-lg" data-testid="monthly-calendar-view">
      <CardContent>
        <div className="tl">
          {fechasOrdenadas.map((fecha) => {
            const clasesDia = clasesPorFecha[fecha];
            const diaPasado = clasesDia.every(c => c.estado === 'caducada');
            return (
              <div key={fecha} className={`nt-day ${diaPasado ? 'past' : ''}`}>
                <h3>{formatFullDate(fecha)}</h3>
                <div className="space-y-2">
                  {clasesDia.map((clase, idx) => {
                    const esClasePasada = clase.estado === 'caducada';
                    const esCambiada = !!clase.cambio_id && !esClasePasada;
                    // Festivo/bloqueado (del configurador): se ve en GRIS ("no hay clase").
                    // Recuperable → aún se puede cambiar por otro día; no_recuperable → no.
                    const esFestivo = !!clase.bloqueado_festivo && !esClasePasada && !esCambiada;
                    // Clase bonificada (regalo) canjeada por el cliente · azul cielo.
                    const esBonificada = clase.tipo === 'bonificada' && !esClasePasada;
                    const esBloqueada = !esClasePasada && !esCambiada && !esBonificada && clase.editable === false;
                    // Editable = misma lógica ORIGINAL: cualquier clase no pasada, con editable !== false
                    // y crédito disponible (incluye las CAMBIADAS y los festivos RECUPERABLES).
                    // La BONIFICADA se puede mover SIN gastar un cambio (no depende del crédito).
                    const esEditable = !esClasePasada && clase.editable !== false && (esBonificada || cambiosDisponibles > 0);
                    // La bonificada reservada se pinta como una CAMBIADA (naranja) pero etiquetada.
                    const stateClass = (esClasePasada || esBloqueada || esFestivo) ? 'done'
                      : (esCambiada || esBonificada) ? 'changed' : 'fixed';

                    const graceRemaining = getGraceRemaining(clase);
                    const showGraceButton = clase.is_in_grace_period && graceRemaining > 0;

                    let motivoBloqueo;
                    if (esClasePasada) motivoBloqueo = t('cal.yaRealizadaTitle');
                    else if (!clase.editable) motivoBloqueo = clase.motivo_no_editable || t('cal.noEditable');
                    else if (cambiosDisponibles === 0) motivoBloqueo = t('cal.sinCredito');
                    else motivoBloqueo = t('cal.cambiarClase');

                    return (
                      <div key={idx} className="space-y-2">
                        <button
                          type="button"
                          className={`nt-slot ${stateClass}`}
                          data-testid={`clase-${fecha}-${clase.hora}`}
                          onClick={() => esEditable && onClaseClick && onClaseClick(clase)}
                          disabled={!esEditable}
                          title={motivoBloqueo}
                        >
                          <span className="nt-ic">
                            {esClasePasada ? <Check className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                          </span>
                          <div className="nt-body">
                            <div className="nt-time">{clase.hora}</div>
                            <div className="nt-sub">
                              {esClasePasada ? t('cal.yaRealizada')
                                : esBonificada ? t('cal.bonificadaRegalo')
                                : esCambiada ? t('cal.claseCambiada')
                                : esFestivo ? (clase.no_recuperable ? t('cal.festivoNoRec') : t('cal.festivoRec'))
                                : esBloqueada ? (clase.motivo_no_editable || t('cal.noEditableCorto'))
                                : 'Método Pilar Domínguez'}
                            </div>
                          </div>
                          <span className="nt-badge">
                            {esClasePasada ? t('cal.badgeRealizada') : esBonificada ? t('cal.badgeBonificada') : esCambiada ? t('cal.badgeCambiada') : esFestivo ? t('cal.badgeFestivo') : esBloqueada ? t('cal.badgeBloqueada') : t('cal.badgeFija')}
                          </span>
                        </button>

                        {/* Botón de rectificación en periodo de gracia */}
                        {showGraceButton && (
                          <button
                            onClick={() => onClaseClick && onClaseClick(clase)}
                            className="w-full p-2 rounded-lg bg-blue-50 border-2 border-blue-400 text-blue-700 hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span className="text-sm font-semibold">
                              {t('cal.rectificar', { tiempo: formatGraceTime(graceRemaining) })}
                            </span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="nt-legend nt-legend--foot">
            <span><i className="nt-l-fix" /> {t('cal.badgeFija')}</span>
            <span><i className="nt-l-chg" /> {t('cal.badgeCambiada')}</span>
            <span><i className="nt-l-done" /> {t('cal.legendRealizada')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyCalendarView;
